<template>
  <div id="app">
    <component :is="layout">
      <router-view :key="$route.fullPath" />
    </component>
    <vue-progress-bar></vue-progress-bar>

    <transition name="fade">
      <PopupUpdate
        v-if="showUpdateUI"
        @update="accept"
        @close="closePopup" />
    </transition>
  </div>
</template>

<script>
import LayoutDefault from '@/layouts/Default.vue';
import LayoutSimple from '@/layouts/Simple.vue';
import PopupUpdate from '@/components/common/PopupUpdate.vue';

export default {
  name: 'PilihLaptop',

  data() {
    return {
      showUpdateUI: false,
    };
  },

  components: {
    LayoutDefault,
    LayoutSimple,
    PopupUpdate,
  },

  computed: {
    layout() {
      if (this.$route.meta.layout === 'simple') return LayoutSimple;
      return LayoutDefault;
    },

    lang() {
      return this.$store.state.locale;
    },

    metaData() {
      return {
        siteName: process.env.VUE_APP_SITENAME,
        title: `${this.$t('about.title')} - ${process.env.VUE_APP_SITENAME}`,
        description: this.$t('about.description'),
        image: 'https://pilihlaptop.com/img/pilihlaptop-logo-min.png',
      };
    },
  },

  mounted() {
    this.$Progress.finish();
  },

  created() {
    this.init();
    this.$Progress.start();

    this.$router.beforeEach((to, from, next) => {
      if (to.meta.progress !== undefined) {
        const meta = to.meta.progress;
        this.$Progress.parseMeta(meta);
      }

      this.$Progress.start();
      next();
    });

    this.$router.afterEach(() => {
      this.$Progress.finish();
    });
  },

  methods: {
    init() {
      this.setLocale();

      if (this.$workbox) {
        this.$workbox.addEventListener('waiting', () => {
          this.showUpdateUI = true;
        });
      }
    },

    setLocale() {
      const { locale } = this.$store.state;
      this.$i18n.locale = locale;
      document.querySelector('html').setAttribute('lang', locale);
    },

    closePopup() {
      this.showUpdateUI = false;
    },

    async accept() {
      this.showUpdateUI = false;
      await this.$workbox.messageSW({ type: 'SKIP_WAITING' });
    },
  },

  metaInfo() {
    return {
      title: this.$t('about.title'),
      titleTemplate: `%s - ${process.env.VUE_APP_SITENAME}`,
      meta: [
        { name: 'description', content: this.metaData.description },
        { name: 'robots', content: 'index,follow' },

        { property: 'og:title', content: this.metaData.title },
        { property: 'og:site_name', content: this.metaData.siteName },
        { property: 'og:description', content: this.metaData.description },
        { property: 'og:image', content: this.metaData.image },
        { property: 'og:type', content: 'website' },

        { name: 'twitter:card', content: 'summary' },
        { name: 'twitter:title', content: this.metaData.title },
        { name: 'twitter:description', content: this.metaData.description },
        { name: 'twitter:image', content: this.metaData.image },
        { name: 'twitter:url', content: 'https://twitter.com/PilihLaptop' },
      ],
      link: [
        { rel: 'canonical', href: 'https://www.pilihlaptop.com' },
      ],
      htmlAttrs: {
        lang: this.lang,
      },
    };
  },
};
</script>

<style lang="scss">
  @import 'assets/scss/app';
</style>
